import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { User, UserRoleEnum } from 'graphql-common';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import SupportiveMessage, {
  SupportiveMessageColor,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import Button from '@lib/components/Button/Button';
import styles from './UserInfoView.module.scss';

export default function UserInfoView({
  user,
  openTeamPage,
  onEdit,
}: {
  user: User;
  openTeamPage: (id: string) => void;
  onEdit?: () => void;
}) {
  const { t } = useTranslation();
  const {
    email,
    sites,
    personalId,
    phoneNumber,
    team,
    deactivatedAt,
    role,
    sectors,
  } = user;
  const isFullAdmin = role === UserRoleEnum.FullAdmin;

  let sitesRender;
  if (sites?.collection?.length) {
    sitesRender = sites.collection.map((item) => (
      <Chip key={item.id} type={ChipTypes.chipGray} label={item.name} />
    ));
  }

  let sectorsRender;
  if (sectors?.collection?.length) {
    sectorsRender = sectors.collection.map((item) => (
      <Chip
        key={item.id}
        type={(item.color as ChipTypes) || ChipTypes.chipGray}
        label={item.name}
        leftIcon="circle"
      />
    ));
  }

  return (
    <div>
      <div className={classNames(entityViewStyles.infoList, styles.infoList)}>
        <div className={entityViewStyles.infoItem}>
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="track_changes" symbolsOutlined />
            <Typography variant="caption">{t('status')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            {deactivatedAt ? (
              <Chip
                type={ChipTypes.chipRed}
                label={t('deactivated')}
                leftIcon="do_not_disturb_on"
              />
            ) : (
              <Chip
                type={ChipTypes.chipGreen}
                label={t('active')}
                leftIcon="circle"
              />
            )}
          </div>
        </div>
        <div className={entityViewStyles.infoItem}>
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="password" symbolsOutlined />
            <Typography variant="caption">{t('personal-id')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            <Typography variant="body">{personalId || '-'}</Typography>
          </div>
        </div>
        <div className={entityViewStyles.infoItem}>
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="mail" symbolsOutlined />
            <Typography variant="caption">{t('email')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            <Typography variant="body">{email || '-'}</Typography>
          </div>
        </div>
        <div className={entityViewStyles.infoItem}>
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="call" symbolsOutlined />
            <Typography variant="caption">{t('phone-number')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            <Typography variant="body">{phoneNumber || '-'}</Typography>
          </div>
        </div>
        {isFullAdmin ? (
          <div className={entityViewStyles.infoItem}>
            <p className={entityViewStyles.infoItemLabel}>
              <MaterialIcon icon="home_pin" symbolsOutlined />
              <Typography variant="caption">
                {t('site')} / {t('sector')}
              </Typography>
            </p>
            <div
              className={classNames(
                entityViewStyles.infoItemContent,
                entityViewStyles.infoItemContentRow,
              )}
            >
              <Chip type={ChipTypes.chipGray} label={t('all')} />
            </div>
          </div>
        ) : (
          <div
            className={classNames(
              entityViewStyles.infoItem,
              entityViewStyles.infoItemCentered,
            )}
          >
            <p className={entityViewStyles.infoItemLabel}>
              <MaterialIcon icon="home_pin" symbolsOutlined />
              <Typography variant="caption">
                {t('site')} / {t('sector')}
              </Typography>
            </p>
            {sitesRender || sectorsRender ? (
              <div
                className={classNames(
                  entityViewStyles.infoItemContent,
                  entityViewStyles.infoItemContentRow,
                )}
              >
                {sitesRender}
                {sectorsRender}
              </div>
            ) : (
              <div className={entityViewStyles.infoItemContentBox}>
                <SupportiveMessage
                  color={SupportiveMessageColor.Yellow}
                  text={t('user-have-no-access-site-sector')}
                  actions={
                    onEdit ? (
                      <Button buttonText={t('edit')} onClick={onEdit} />
                    ) : undefined
                  }
                  className={entityViewStyles.infoItemContentFullWidth}
                />
              </div>
            )}
          </div>
        )}
        <div className={entityViewStyles.infoItem}>
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="group" symbolsOutlined />
            <Typography variant="caption">{t('team')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            {team?.id ? (
              <Chip
                label={team.name}
                type={(team.color as ChipTypes) || ChipTypes.chipGray}
                leftIcon="circle"
                onClick={() => openTeamPage(team.id)}
              />
            ) : (
              <Typography variant="body">-</Typography>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
